import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiHeart, FiShoppingCart, FiZap } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

export default function ProductCard({ product, onRemoveWishlist }) {
  const { name, slug, base_price, sale_price, current_price, primary_image, category_name, id, variants } = product;
  const onSale = sale_price && sale_price < base_price;
  const discountPercent = onSale ? Math.round(((base_price - sale_price) / base_price) * 100) : 0;
  const sizes = variants ? [...new Set(variants.filter(v => v.is_in_stock).map(v => v.size))] : [];
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [wishlisted, setWishlisted] = useState(false);
  const [wishId, setWishId] = useState(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get('auth/wishlist/').then(r => {
      const items = r.data.results || r.data;
      const found = items.find(w => w.product === id);
      if (found) { setWishlisted(true); setWishId(found.id); }
    }).catch(() => {});
  }, [user, id]);

  const getDefaultVariant = () => {
    if (!variants || variants.length === 0) return null;
    return variants.find(v => v.is_in_stock) || variants[0];
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error('Please login first'); navigate('/login'); return; }
    const variant = getDefaultVariant();
    if (!variant) { toast.error('Out of stock'); return; }
    try {
      await addToCart(variant.id, 1);
      toast.success('Added to cart');
    } catch { toast.error('Failed to add'); }
  };

  const handleBuyNow = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error('Please login first'); navigate('/login'); return; }
    const variant = getDefaultVariant();
    if (!variant) { toast.error('Out of stock'); return; }
    try {
      await addToCart(variant.id, 1);
      navigate('/checkout');
    } catch { toast.error('Failed'); }
  };

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error('Login to add to wishlist'); return; }
    try {
      if (wishlisted && wishId) {
        await api.delete(`auth/wishlist/${wishId}/`);
        setWishlisted(false); setWishId(null);
        toast.success('Removed from wishlist');
        if (onRemoveWishlist) onRemoveWishlist(id);
      } else {
        const { data } = await api.post('auth/wishlist/', { product: id });
        setWishlisted(true); setWishId(data.id);
        toast.success('Added to wishlist');
      }
      window.dispatchEvent(new Event('wishlist-updated'));
    } catch { toast.error('Already in wishlist'); }
  };

  return (
    <div style={{ position: 'relative' }}>
      {onSale && discountPercent > 0 && (
        <span style={{ position: 'absolute', top: 10, left: 10, zIndex: 2, background: '#C9A96E', color: '#fff', fontSize: 11, fontWeight: 600, padding: '4px 8px', letterSpacing: 1 }}>
          {discountPercent}% OFF
        </span>
      )}
      <button onClick={toggleWishlist} aria-label="Wishlist" style={{
        position: 'absolute', top: 10, right: 10, zIndex: 2, background: 'rgba(255,255,255,0.9)',
        border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex',
        alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.3s',
      }}>
        <FiHeart size={16} style={{ fill: wishlisted ? '#C9A96E' : 'none', color: wishlisted ? '#C9A96E' : '#333' }} />
      </button>
      <Link to={`/product/${slug}`} style={{ textDecoration: 'none', color: '#000' }}>
        <div
          style={{ overflow: 'hidden', marginBottom: 15, position: 'relative' }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <img src={primary_image} alt={name} style={{ width: '100%', height: 380, objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease', transform: hovered ? 'scale(1.05)' : 'scale(1)' }} />
          {hovered && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              display: 'flex', gap: 0,
              animation: 'fadeUp 0.2s ease',
            }}>
              <button onClick={handleAddToCart} style={{
                flex: 1, padding: '12px 0', background: '#1a1a1a', color: '#fff',
                border: 'none', fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <FiShoppingCart size={13} /> Add to Cart
              </button>
              <button onClick={handleBuyNow} style={{
                flex: 1, padding: '12px 0', background: '#C9A96E', color: '#fff',
                border: 'none', fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <FiZap size={13} /> Buy Now
              </button>
            </div>
          )}
        </div>
        <p style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 5 }}>{category_name}</p>
        <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 500, marginBottom: 8 }}>{name}</h3>
        <div style={{ fontSize: 14, marginBottom: 8 }}>
          {onSale && <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 10 }}>₹{base_price}</span>}
          <span style={{ color: onSale ? '#C9A96E' : '#000', fontWeight: onSale ? 600 : 400 }}>₹{current_price}</span>
          {onSale && <span style={{ marginLeft: 8, color: '#4caf50', fontSize: 12, fontWeight: 600 }}>Save ₹{(base_price - sale_price).toFixed(0)}</span>}
        </div>
      </Link>
      {sizes.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
          {sizes.slice(0, 6).map(s => (
            <Link key={s} to={`/product/${slug}`} style={{ padding: '3px 8px', border: '1px solid #ddd', fontSize: 10, color: '#555', textDecoration: 'none', letterSpacing: 0.5 }}>{s}</Link>
          ))}
        </div>
      )}
    </div>
  );
}
